import axios from 'axios';
import { Workplace, User, SubscriptionStatus, Visit } from '../types';

const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && window.location?.hostname) {
    // Hem bilgisayarda (localhost) hem de cep telefonunda (192.168.x.x vb.) aynı sunucunun 5001 portuna bağlanır
    return `http://${window.location.hostname}:5001/api/v1`;
  }
  return 'http://localhost:5001/api/v1';
};

const API_BASE_URL = getApiBaseUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// İstek öncesi Token ekleme
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('field_uzman_token') || localStorage.getItem('uzman_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // FormData gönderilirken browser'ın boundary içeren Content-Type oluşturmasına izin ver
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Yanıt yakalayıcı (401 oturum süresi dolumu)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      if (!pathname.includes('/login') && !pathname.includes('/register') && !pathname.includes('/field/login')) {
        localStorage.removeItem('uzman_token');
        localStorage.removeItem('uzman_user');
        localStorage.removeItem('field_uzman_token');
        localStorage.removeItem('field_uzman_user');
        window.location.href = pathname.startsWith('/field') ? '/field/login' : '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth Servisleri
export const authApi = {
  register: async (data: { 
    fullName: string; 
    email: string; 
    phone?: string; 
    password: string; 
    kvkkConsent: boolean;
    commercialConsent?: boolean;
  }) => {
    const res = await apiClient.post('/auth/register', data);
    return res.data;
  },
  verifyEmail: async (token: string) => {
    const res = await apiClient.get(`/auth/verify-email?token=${token}`);
    return res.data;
  },
  login: async (credentials: { email: string; password: string }) => {
    const res = await apiClient.post('/auth/login', credentials);
    return res.data;
  },
  forgotPassword: async (email: string) => {
    const res = await apiClient.post('/auth/forgot-password', { email });
    return res.data;
  },
  resetPassword: async (data: { token: string; newPassword: string }) => {
    const res = await apiClient.post('/auth/reset-password', data);
    return res.data;
  },
  getMe: async () => {
    const res = await apiClient.get('/auth/me');
    return res.data;
  },
  mobileLogin: async (data: { phoneOrEmail: string; password: string }) => {
    const res = await apiClient.post('/auth/mobile-login', data);
    return res.data;
  },
  mobileChangePin: async (data: { newPassword: string }, tempToken?: string) => {
    const headers = tempToken ? { Authorization: `Bearer ${tempToken}` } : {};
    const res = await apiClient.post('/auth/mobile-change-pin', data, { headers });
    return res.data;
  },
  ping: async () => {
    const res = await apiClient.get('/auth/ping');
    return res.data;
  },
  updateProfile: async (data: {
    fullName?: string;
    phone?: string;
    currentPassword?: string;
    newPassword?: string;
    mobilePin?: string;
  }) => {
    const res = await apiClient.put('/auth/profile', data);
    return res.data;
  }
};

// Mobil PWA Saha Servisleri
export const mobileApi = {
  getDashboard: async () => {
    const res = await apiClient.get('/mobile/dashboard');
    return res.data;
  },
  recordQuickVisit: async (data: { workplaceId: number; notes?: string }) => {
    const res = await apiClient.post('/mobile/quick-visit', data);
    return res.data;
  },
  getWorkplaceDetail: async (id: number) => {
    const res = await apiClient.get(`/mobile/workplace/${id}`);
    return res.data;
  },
  deleteDocument: async (id: number) => {
    const res = await apiClient.delete(`/mobile/documents/${id}`);
    return res.data;
  }
};

// İşyeri Servisleri (Özel İSG Uzmanı izolasyonlu)
export const workplaceApi = {
  getAll: async (params?: { search?: string; dangerClass?: string }) => {
    const res = await apiClient.get<{ success: boolean; count: number; data: Workplace[] }>('/workplaces', { params });
    return res.data;
  },
  getById: async (id: number) => {
    const res = await apiClient.get<{ success: boolean; data: Workplace }>(`/workplaces/${id}`);
    return res.data;
  },
  create: async (data: Partial<Workplace>) => {
    const res = await apiClient.post<{ success: boolean; message: string; data: Workplace }>('/workplaces', data);
    return res.data;
  },
  update: async (id: number, data: Partial<Workplace>) => {
    const res = await apiClient.put<{ success: boolean; message: string; data: Workplace }>(`/workplaces/${id}`, data);
    return res.data;
  },
  delete: async (id: number) => {
    const res = await apiClient.delete<{ success: boolean; message: string }>(`/workplaces/${id}`);
    return res.data;
  },
  uploadDocument: async (id: number, formData: FormData) => {
    const res = await apiClient.post(`/workplaces/${id}/documents`, formData);
    return res.data;
  },
  getDocuments: async (id: number) => {
    const res = await apiClient.get(`/workplaces/${id}/documents`);
    return res.data;
  }
};

// Abonelik & Lisans Servisleri
export const subscriptionApi = {
  getStatus: async () => {
    const res = await apiClient.get<{ success: boolean; data: SubscriptionStatus }>('/subscription/status');
    return res.data;
  },
  requestActivation: async (plan: string) => {
    const res = await apiClient.post<{ success: boolean; message: string }>('/subscription/request-activation', { plan });
    return res.data;
  },
  applyCode: async (code: string) => {
    const res = await apiClient.post<{ success: boolean; message: string; data: any }>('/subscription/apply-code', { code });
    return res.data;
  }
};

// Saha Ziyareti Servisleri (Takvim & Haftalık Program & Mobil PWA)
export const visitApi = {
  create: async (data: {
    workplaceId: number;
    visitDate: string;
    startTime?: string;
    endTime?: string;
    visitType?: string;
    notes?: string;
    status?: string;
    allowConflict?: boolean;
  }) => {
    const res = await apiClient.post<{ success: boolean; message: string; data: Visit }>('/visits', data);
    return res.data;
  },
  getAll: async (params?: { startDate?: string; endDate?: string; status?: string; workplaceId?: number }) => {
    const res = await apiClient.get<{ success: boolean; count: number; data: Visit[] }>('/visits', { params });
    return res.data;
  },
  getByWorkplace: async (workplaceId: number) => {
    const res = await apiClient.get<{ success: boolean; count: number; data: Visit[] }>(`/visits/workplace/${workplaceId}`);
    return res.data;
  },
  getWeekly: async (startDate?: string, endDate?: string) => {
    const res = await apiClient.get<{ success: boolean; count: number; startDate: string; endDate: string; data: Visit[] }>('/visits/weekly', {
      params: { startDate, endDate }
    });
    return res.data;
  },
  updateStatus: async (id: number, data: { status?: 'planned' | 'completed' | 'cancelled'; notes?: string; visitDate?: string }) => {
    const res = await apiClient.put<{ success: boolean; message: string; data: Visit }>(`/visits/${id}`, data);
    return res.data;
  },
  delete: async (id: number) => {
    const res = await apiClient.delete<{ success: boolean; message: string }>(`/visits/${id}`);
    return res.data;
  }
};

// ============================================================================
// ARŞİV VE KLASÖR YÖNETİMİ SERVİSLERİ (İSG DESTEK WEB PWA İLE BİREBİR UYUMLU)
// ============================================================================

export interface WorkplaceDocumentItem {
  id: number;
  documentType: string;
  originalFileName: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedDate: string;
  documentDate?: string;
  expiryDate?: string;
  expiryStatus: 'valid' | 'expiring' | 'expired' | 'permanent';
  daysUntilExpiry: number | null;
  workplaceId: number;
  version: number;
  tags?: string[];
  notes?: string;
  isArchived: boolean;
  viewCount: number;
  Workplace?: {
    id: number;
    title: string;
    dangerClass: string;
    sgkNo?: string;
  };
  workplace?: {
    id: number;
    title: string;
    dangerClass?: string;
    sgkNo?: string;
  };
}

export interface ArchiveStats {
  total: number;
  byType: Record<string, number>;
  byStatus: {
    valid: number;
    expiring: number;
    expired: number;
    permanent: number;
  };
  archived: number;
  totalViews: number;
  recentUploads: number;
}

export interface FolderNode {
  id: number;
  name: string;
  type: string;
  parentFolderId: number | null;
  parent?: { id: number; name: string; type: string } | null;
  children?: FolderNode[];
  docCount?: number;
}

export const archiveApi = {
  getWorkplacesLookup: async (search?: string) => {
    const res = await apiClient.get<Array<{ id: number; title: string; sgkNo: string; dangerClass: string; city?: string; district?: string }>>(
      '/archive/workplaces-lookup',
      { params: { search } }
    );
    return res.data;
  },
  getAllDocuments: async (params?: any) => {
    const res = await apiClient.get<WorkplaceDocumentItem[]>('/archive', { params });
    return res.data;
  },
  getStats: async () => {
    const res = await apiClient.get<ArchiveStats>('/archive/stats');
    return res.data;
  },
  downloadDocument: async (id: number, originalFileName: string) => {
    const res = await apiClient.get(`/archive/${id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', originalFileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  previewDocument: async (id: number) => {
    const res = await apiClient.get(`/archive/${id}/preview`, { responseType: 'blob' });
    return window.URL.createObjectURL(new Blob([res.data], { type: String(res.headers['content-type'] || 'application/pdf') }));
  },
  deleteDocument: async (id: number) => {
    const res = await apiClient.delete<{ message: string }>(`/archive/${id}`);
    return res.data;
  },
  bulkDownload: async (documentIds: number[]) => {
    const res = await apiClient.post('/archive/bulk-download', { documentIds }, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `arsiv_belgeleri_${Date.now()}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  bulkDelete: async (documentIds: number[]) => {
    const res = await apiClient.delete<{ message: string }>('/archive/bulk-delete', { data: { documentIds } });
    return res.data;
  }
};

export const folderApi = {
  getWorkplaceFolders: async (workplaceId: number) => {
    const res = await apiClient.get<FolderNode[]>(`/folders/workplace/${workplaceId}`);
    return res.data;
  },
  getFolderContent: async (folderId: number) => {
    const res = await apiClient.get<{ folder: any; ancestors?: any[]; subFolders: any[]; documents: WorkplaceDocumentItem[] }>(`/folders/${folderId}`);
    return res.data;
  },
  getWorkplaceRootContent: async (workplaceId: number) => {
    const res = await apiClient.get<{ subFolders: any[]; documents: WorkplaceDocumentItem[] }>(`/folders/workplace/${workplaceId}/root`);
    return res.data;
  },
  createFolder: async (data: { name: string; workplaceId: number; parentFolderId?: number }) => {
    const res = await apiClient.post<FolderNode>('/folders', data);
    return res.data;
  }
};

export const workplaceDocumentApi = {
  uploadDocument: async (workplaceId: number, formData: FormData) => {
    const res = await apiClient.post<{ success: boolean; message: string; data: WorkplaceDocumentItem }>(
      `/workplaces/${workplaceId}/documents`,
      formData
    );
    return res.data;
  },
  getWorkplaceDocuments: async (workplaceId: number) => {
    const res = await apiClient.get<{ success: boolean; data: WorkplaceDocumentItem[] }>(`/workplaces/${workplaceId}/documents`);
    return res.data;
  }
};

// Yasal Belge Üretim Araçları Servisleri (Yıllık Çalışma & Eğitim Planları)
export const documentToolsApi = {
  getAnnualPlan: async (workplaceId: number, year?: number) => {
    const res = await apiClient.get(`/workplaces/${workplaceId}/annual-plan`, { params: { year } });
    return res.data;
  },
  saveAnnualPlan: async (workplaceId: number, data: any) => {
    const res = await apiClient.post(`/workplaces/${workplaceId}/annual-plan`, data);
    return res.data;
  },
  downloadAnnualPlanDocx: async (workplaceId: number, year: number, matrix: any, fileName: string) => {
    const res = await apiClient.post(`/workplaces/${workplaceId}/annual-plan/docx`, { year, matrix }, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  getAnnualTrainingPlan: async (workplaceId: number, year?: number) => {
    const res = await apiClient.get(`/workplaces/${workplaceId}/annual-training-plan`, { params: { year } });
    return res.data;
  },
  saveAnnualTrainingPlan: async (workplaceId: number, data: any) => {
    const res = await apiClient.post(`/workplaces/${workplaceId}/annual-training-plan`, data);
    return res.data;
  },
  downloadAnnualTrainingPlanDocx: async (workplaceId: number, year: number, matrix: any, fileName: string) => {
    const res = await apiClient.post(`/workplaces/${workplaceId}/annual-training-plan/docx`, { year, matrix }, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

export const emergencyPlanApi = {
  generateDocx: async (payload: any): Promise<Blob> => {
    const res = await apiClient.post('/documents/emergency-plan/generate-docx', payload, {
      responseType: 'blob'
    });
    return res.data;
  }
};

export const riskAnalysisApi = {
  getSectors: async () => {
    const res = await apiClient.get<{ sectors: any[] }>('/risk-analysis/sectors');
    return res.data;
  },
  getSectorRisks: async (sectorId: string) => {
    const res = await apiClient.get<{ sektor: string; sektorAdi: string; riskler: any[] }>(`/risk-analysis/sectors/${sectorId}`);
    return res.data;
  },
  downloadPdf: async (data: any, fileName: string) => {
    const res = await apiClient.post('/risk-analysis/pdf', data, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  downloadExcel: async (data: any, fileName: string) => {
    const res = await apiClient.post('/risk-analysis/excel', data, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

// Certificate API
export const certificateApi = {
  generateCertificates: async (excelFile: File): Promise<Blob> => {
    const formData = new FormData();
    formData.append('excel_data', excelFile);
    const res = await apiClient.post('/certificates/generate', formData, {
      responseType: 'blob'
    });
    return res.data;
  },
  uploadTemplate: async (templateFile: File): Promise<{ success: boolean; message: string }> => {
    const formData = new FormData();
    formData.append('template', templateFile);
    const res = await apiClient.post('/certificates/template', formData);
    return res.data;
  },
  downloadExcelTemplate: async (): Promise<Blob> => {
    const res = await apiClient.get('/certificates/excel-template', {
      responseType: 'blob'
    });
    return res.data;
  },
  downloadDocxTemplate: async (): Promise<Blob> => {
    const res = await apiClient.get('/certificates/template', {
      responseType: 'blob'
    });
    return res.data;
  }
};

export const generateCertificates = certificateApi.generateCertificates;
export const uploadCertificateTemplate = certificateApi.uploadTemplate;
export const downloadCertificateExcelTemplate = certificateApi.downloadExcelTemplate;
export const downloadCertificateDocxTemplate = certificateApi.downloadDocxTemplate;

// Generic apiRequest helper for SubscriptionPage and other components
export const apiRequest = async (endpoint: string, method: string = 'GET', data?: any) => {
  const url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const config = {
    method: method.toLowerCase(),
    url,
    ...(data && method.toUpperCase() !== 'GET' ? { data } : {}),
    ...(data && method.toUpperCase() === 'GET' ? { params: data } : {})
  };
  const res = await apiClient(config);
  return res.data;
};

export default apiClient;

